import { BusinessType, DATA_AGGREGATOR_OPTIONS, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { BodyText, Button, DetailRow, DetailsCard, Title, UtilButton } from 'UI';
import { useBusinessTypeOptions, useCountryNames } from '@/hooks';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import StepShell from '@/components/Entities/Accounts/AccountCreation/StepShell/StepShell';
import usePlatformUsageOptions from './usePlatformUsageOptions';
import useInheritedSettingsRows from './useInheritedSettingsRows';
import { ParentAccountOption, formatParentAccountLabel } from './parentAccountOptions';
import type { BillingPackageOption, DetailsFormValues, SettingsFormValues } from './types';

import styles from './ReviewStep.module.scss';

const cn = withStyles(styles);

type ReviewStepProps = {
  steps: string[];
  currentStep: number;
  details: DetailsFormValues | null;
  settings: SettingsFormValues | null;
  businessTypes: { [businessTypeKey: string]: BusinessType };
  /** The parent picked on the Details step; set, this reviews a sub-account (spec 5.3, Step 3). */
  parentAccount: ParentAccountOption | null;
  /** A sub-account creation the backend refused: shown inline, the user edits and retries (spec 5.5). */
  submitError: string;
  billingPackageOptions: BillingPackageOption[];
  /** The review copy only promises the onboarding email where it will actually send. */
  willSendOnboardingEmail: boolean;
  goToStep: (step: 'details' | 'settings') => void;
  onSubmit: () => void;
};

export default function ReviewStep({
  steps,
  currentStep,
  details,
  settings,
  businessTypes,
  parentAccount,
  submitError,
  billingPackageOptions,
  willSendOnboardingEmail,
  goToStep,
  onSubmit,
}: ReviewStepProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Review' });
  const { t: tDetails } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Details' });
  const { t: tSettings } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Settings' });
  const { t: tInherited } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Inherited' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const businessTypeOptions = useBusinessTypeOptions();
  const { countryOptions } = useCountryNames();
  const inheritedRows = useInheritedSettingsRows(parentAccount);

  const { canSeeBusinessCase, canAccessCustomServiceRequirements, partnerBillsAccount } =
    useModuleFeatureFlag('accounts');

  const platformUsageOptions = usePlatformUsageOptions();

  const showBillingPackage = !canAccessCustomServiceRequirements && !partnerBillsAccount;

  const selectedIndustry = Object.values(businessTypes).find(
    (businessType) => businessType.BusinessTypeID === details?.industry,
  );

  const onOff = (isEnabled: boolean | undefined) =>
    isEnabled ? t('on', { defaultValue: 'On' }) : t('off', { defaultValue: 'Off' });

  const reviewSection = (
    title: string,
    targetStep: 'details' | 'settings',
    rows: { label: string; value: string }[],
  ) => (
    <DetailsCard>
      <div className={cn('sectionHeader')}>
        <Title level="4">{title}</Title>

        <UtilButton iconLeft="edit" onClick={() => goToStep(targetStep)}>
          {tGlobal('Buttons.edit', { defaultValue: 'Edit' })}
        </UtilButton>
      </div>

      {rows.map(({ label, value }) => (
        <DetailRow key={label} label={label} value={value || '-'} />
      ))}
    </DetailsCard>
  );

  const submitErrorMessage = submitError && (
    <div className={cn('submitError')} role="alert" data-testid="createSubAccountError">
      <BodyText size="sm">{submitError}</BodyText>
    </div>
  );

  if (parentAccount) {
    return (
      <StepShell
        title={t('title', { defaultValue: 'Review' })}
        description={t('subAccountDescription', {
          defaultValue: 'Confirm the details below before creating the sub-account.',
        })}
        steps={steps}
        currentStep={currentStep}
        footer={
          <>
            <Button colorScheme="secondary" type="button" onClick={() => goToStep('settings')}>
              {tGlobal('Buttons.back', { defaultValue: 'Back' })}
            </Button>

            {/* In the sticky footer, beside the button that raised it, so it is seen wherever the
                review is scrolled to. */}
            {submitErrorMessage}

            <Button colorScheme="primary" type="button" onClick={onSubmit}>
              {t('createSubAccount', { defaultValue: 'Create Sub-Account' })}
            </Button>
          </>
        }
      >
        {reviewSection(tDetails('parentAccount', { defaultValue: 'Parent Account' }), 'details', [
          {
            label: tDetails('parentAccount', { defaultValue: 'Parent Account' }),
            value: formatParentAccountLabel(parentAccount),
          },
        ])}

        {reviewSection(tDetails('businessInfo', { defaultValue: 'Business Information' }), 'details', [
          {
            label: tDetails('legalName', { defaultValue: 'Legal Business Name' }),
            value: details?.legalBusinessName ?? '',
          },
          { label: tDetails('dba', { defaultValue: 'Doing Business As (DBA Name)' }), value: details?.dbaName ?? '' },
          { label: tDetails('email', { defaultValue: 'Email Address' }), value: details?.emailAddress ?? '' },
          // Asked for under a US parent only (the backend requires it for a US account).
          ...(parentAccount.country === 'US'
            ? [
                {
                  label: tDetails('phone', { defaultValue: 'Business Phone Number' }),
                  value: details?.businessPhone ?? '',
                },
              ]
            : []),
          // Spec 5.3: the sub-account's portal user is always an Admin — shown here, read-only, since
          // there is nothing to choose.
          { label: t('role', { defaultValue: 'Role' }), value: t('roleAdmin', { defaultValue: 'Admin' }) },
        ])}

        {reviewSection(
          tInherited('title', { defaultValue: 'Inherited from {{parentName}}', parentName: parentAccount.accountName }),
          'settings',
          inheritedRows,
        )}
      </StepShell>
    );
  }

  return (
    <StepShell
      title={t('title', { defaultValue: 'Review' })}
      description={t('description', { defaultValue: 'Confirm the details below before creating the account.' })}
      steps={steps}
      currentStep={currentStep}
      footer={
        <>
          <Button colorScheme="secondary" type="button" onClick={() => goToStep('settings')}>
            {tGlobal('Buttons.back', { defaultValue: 'Back' })}
          </Button>

          <Button colorScheme="primary" type="button" onClick={onSubmit}>
            {t('createAccount', { defaultValue: 'Create Account' })}
          </Button>
        </>
      }
    >
      {reviewSection(tDetails('businessInfo', { defaultValue: 'Business Information' }), 'details', [
        {
          label: tDetails('legalName', { defaultValue: 'Legal Business Name' }),
          value: details?.legalBusinessName ?? '',
        },
        { label: tDetails('dba', { defaultValue: 'Doing Business As (DBA Name)' }), value: details?.dbaName ?? '' },
        {
          label: tDetails('businessType', { defaultValue: 'Business Type' }),
          value: businessTypeOptions[details?.businessType ?? ''] ?? '',
        },
        {
          label: tDetails('country', { defaultValue: 'Country' }),
          value: countryOptions[details?.country ?? ''] ?? '',
        },
        {
          label: tDetails('industry', { defaultValue: 'Industry' }),
          value: selectedIndustry?.Industry ?? '',
        },
        {
          label: tDetails('industrySubCategory', { defaultValue: 'Industry Sub-Category' }),
          value:
            Object.values(selectedIndustry?.Categories ?? {}).find(
              (category) => category.BusinessTypeCategoryID === details?.industrySubCategory,
            )?.Category ?? '',
        },
        { label: tDetails('website', { defaultValue: 'Company Website' }), value: details?.websiteURL ?? '' },
        {
          label: tDetails('businessEmail', { defaultValue: 'Business Email' }),
          value: details?.businessEmailAddress ?? '',
        },
        { label: tDetails('phone', { defaultValue: 'Business Phone Number' }), value: details?.businessPhone ?? '' },
        {
          label: tDetails('flowOfFunds', { defaultValue: 'Flow of Funds Diagram' }),
          value: details?.flowOfFundsFileName ?? '',
        },
        ...(canSeeBusinessCase
          ? [
              {
                label: tDetails('businessModel', { defaultValue: 'Business Model' }),
                value: details?.businessModel ?? '',
              },
              {
                label: tDetails('numberOfYears', { defaultValue: 'Number of Years in Business' }),
                value: details?.numberOfYears ?? '',
              },
              {
                label: tDetails('socialMedia', { defaultValue: 'Business Social Media' }),
                value: details?.socialMediaPage ?? '',
              },
              {
                label: tDetails('isMSB', { defaultValue: 'Is this a money service business (MSB)?' }),
                value: onOff(details?.isMSB),
              },
            ]
          : []),
        { label: tDetails('notes', { defaultValue: 'Notes' }), value: details?.notes ?? '' },
      ])}

      {reviewSection(tDetails('businessAddress', { defaultValue: 'Business Address' }), 'details', [
        { label: tDetails('address', { defaultValue: 'Address' }), value: details?.address ?? '' },
        { label: tDetails('unit', { defaultValue: 'Unit' }), value: details?.unit ?? '' },
        { label: tDetails('city', { defaultValue: 'City' }), value: details?.city ?? '' },
        { label: tDetails('provinceState', { defaultValue: 'Province/State' }), value: details?.province ?? '' },
        { label: tDetails('postalZip', { defaultValue: 'Postal Code/Zip Code' }), value: details?.postalCode ?? '' },
      ])}

      {reviewSection(tDetails('primaryContact', { defaultValue: 'Primary Contact Information' }), 'details', [
        {
          label: t('contactName', { defaultValue: 'Name' }),
          value: `${details?.firstName ?? ''} ${details?.lastName ?? ''}`.trim(),
        },
        { label: tDetails('email', { defaultValue: 'Email Address' }), value: details?.emailAddress ?? '' },
      ])}

      {reviewSection(tSettings('title', { defaultValue: 'Settings' }), 'settings', [
        {
          label: tSettings('subAccounts', { defaultValue: 'Sub-Accounts' }),
          value: onOff(settings?.isSubAccountsEnabled),
        },
        {
          label: tSettings('clientAccounts', { defaultValue: 'Client Accounts' }),
          value: onOff(settings?.isClientAccountsEnabled),
        },
        {
          label: tSettings('platformUsage', { defaultValue: 'Platform Usage' }),
          value:
            platformUsageOptions.find((option) => option.value === settings?.platformUsage)?.label ??
            settings?.platformUsage ??
            '',
        },
        { label: tSettings('accountSystem', { defaultValue: 'Account System' }), value: settings?.accountSystem ?? '' },
        {
          label: tSettings('dataAggregator', { defaultValue: 'Data Aggregator' }),
          value: DATA_AGGREGATOR_OPTIONS[settings?.dataAggregator ?? ''] ?? '',
        },
        ...(showBillingPackage
          ? [
              {
                label: tSettings('billingPackage', { defaultValue: 'Billing Package' }),
                value: billingPackageOptions.find((option) => option.value === settings?.packageType)?.label ?? '',
              },
            ]
          : []),
      ])}

      <p className={cn('pendingNote')}>
        {willSendOnboardingEmail
          ? t('pendingNote', {
              defaultValue:
                'Once created, the account enters Application Pending and the onboarding application form is sent to the primary contact. It activates after the application is completed and reviewed.',
            })
          : t('pendingNoteNoEmail', {
              defaultValue: 'Once created, the account enters Application Pending until onboarding is completed.',
            })}
      </p>
    </StepShell>
  );
}
